<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <BreadCrumbs
          v-model="deviceGroupId"
          :modelId="modelId"
          :serialNo="serialNo"
          @click_breadCrumbs="selectedGroupChange"
          @click_breadCrumbsDevice="selectedDeviceChange"
        />
        <v-divider />
      </v-col>
    </v-row>
    <v-row justify="center" align-content="center">
      <v-col cols="12" xl="4" lg="5" md="7" sm="9">
        <v-card outlined>
          <!-- タイトル -->
          <v-card-title>
            <v-row dense>
              <v-col cols="12">
                {{ groupName }}
              </v-col>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </v-row>
          </v-card-title>
          <!-- パラメータ設定 -->
          <v-card-text class="mt-8">
            <v-row dense>
              <v-spacer></v-spacer>
              <v-col class="mt-2 body-1" cols="4" xl="3" lg="3" md="3" sm="4">
                {{ $t('month') }}
              </v-col>
              <v-col class="body-1" cols="4" xl="4" lg="4" md="4" sm="4">
                <Calendar ref="Cal" type="month" v-model="date" allowInput="true" />
              </v-col>
              <v-spacer></v-spacer>
            </v-row>
          </v-card-text>
          <!-- ダウンロード -->
          <v-card-actions class="mb-8">
            <v-row dense>
              <v-spacer></v-spacer>
              <v-col cols="4" xl="3" lg="3" md="3" sm="3">
                <v-btn @click="download" color="primary" block depressed>
                  <custom-icon type="download" :size="15" :colors="['#FFFFFF']" class="mr-2"> </custom-icon>
                  <v-icon>mdi-file-document-multiple-outline</v-icon>
                </v-btn>
              </v-col>
              <v-spacer></v-spacer>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <!-- グラフ -->
    <Chart class="chart" ref="dailyAirConsumptionChart" type="bar" width="1000px" height="300px" />
    <Chart class="chart" ref="monthlyAirConsumptionChart" type="bar" width="1000px" height="300px" />
    <Chart class="chart" ref="dailyPowerConsumptionChart" type="line" width="1000px" height="300px" />
    <Chart class="chart" ref="monthlyPowerConsumptionChart" type="line" width="1000px" height="300px" />
  </v-container>
</template>

<script>
  import exceljs from 'exceljs';
  import CustomIcon from '../components/CustomIcon.vue';
  import Calendar from '../components/Calendar.vue';
  import Chart from '../components/Chart.vue';
  import BreadCrumbs from '../components/BreadCrumbs.vue';
  import monthlyReportTemplateEn from '../resources/monthly_report_template_en.xlsx';
  import monthlyReportTemplateJa from '../resources/monthly_report_template_ja.xlsx';
  import { mapGetters } from 'vuex';
  import logging from '@/utils/logging';

  export default {
    title() {
      return this.$t(this.$route.meta.title);
    },
    name: 'MonthlyReport',
    components: {
      CustomIcon,
      Calendar,
      Chart,
      BreadCrumbs,
    },
    mixins: [logging],
    data: () => ({
      localeId: 2,
      date: null,
      modelId: '',
      serialNo: '',
      deviceGroupId: '',
      deviceGroupName: '',
      groupId: '',
      groupName: '',
      devices: [],
      summaryData: [],
      thisMonthAirConsumption: 0,
      thisMonthPowerConsumption: 0,
      monthlyReportTemplate: null,
    }),
    computed: {
      ...mapGetters('auth', ['user_info']),
    },
    async created() {
      console.log(`created開始：${this.$moment().format('YYYY-MM-DD HH:mm:ss')}`);

      // デフォルト年月日
      this.date = this.$moment().format('YYYY-MM');

      // URLから値を取得
      this.deviceGroupId = this.$route.params.device_group_id;
      this.groupId = this.$route.params.group_id;
      const compressors = this.$route.params.compressors;

      this.$store.commit('loading/start_loading');

      // グループ情報から、model_id、serial_noを取得
      let deviceParams = [];
      if (this.groupId === 'all' || this.groupId === 'unset') {
        if (this.groupId === 'all') {
          this.groupName = this.$t('allDevices');
        } else {
          this.groupName = this.$t('groupNotSet');
        }
        deviceParams = compressors.map((item) => {
          return {
            model_id: item.modelId,
            serial_no: item.serialNo,
          };
        });
        // 個別機械から来たとき
        if (deviceParams.length === 1) {
          this.modelId = deviceParams[0].model_id;
          this.serialNo = deviceParams[0].serial_no;
        }
      } else {
        const group = await this.azureRequest('/api/getGroups', 'GET', { id: this.groupId });
        if (group) {
          // グループが存在するなら、グループに属するデバイスを取得
          this.groupName = group.groupName;

          deviceParams = group.devices.map((device) => {
            return {
              model_id: device.modelId,
              serial_no: device.serialNo,
            };
          });
        } else {
          this.$store.commit('snackbar/show_snackbar', {
            text: this.$t('theGroupDoesNotExist'),
            color: 'error',
            timeout: 5000,
            multiline: false,
          });
          this.$store.commit('loading/end_loading');
          return;
        }
      }

      // ロケール設定
      this.setLocale();

      // デバイスグループを取得
      const deviceGroups = await this.apiRequest('/v1/master-info/userDeviceGroupAuthorities', 'GET', {
        device_group_id: this.deviceGroupId,
      });
      if (deviceGroups === null) {
        this.$store.commit('loading/end_loading');
        return;
      }
      this.deviceGroupName = deviceGroups[0][`device_group_name_locale${this.localeId}`];

      // デバイスを取得
      for (const deviceParam of deviceParams) {
        const devices = await this.apiRequest('/v1/master-info/devices', 'GET', deviceParam);
        if (devices === null) {
          this.$store.commit('loading/end_loading');
          return;
        }
        this.devices.push(devices[0]);
      }
      for (const device of this.devices) {
        device.sensors = [];
        for (const name_locale2 of ['運転時間', '総運転回数', 'LED ランプ状態(Hex)']) {
          const sensors = await this.apiRequest('/v1/master-info/sensors', 'GET', {
            model_id: device.model_id,
            serial_no: device.serial_no,
            name_locale2: name_locale2,
          });
          if (sensors !== null && sensors.length > 0) {
            device.sensors.push(sensors[0]);
          }
        }
      }

      // お客様管理号機, 機械番号で昇順になるようにソート
      this.devices.sort((a, b) => {
        const keyA = `${a.optional_info_2}-${a[`name_locale${this.localeId}`]}`;
        const keyB = `${b.optional_info_2}-${b[`name_locale${this.localeId}`]}`;
        if (keyA < keyB) return -1;
        if (keyA > keyB) return 1;
        return 0;
      });

      // グラフ設定
      this.setChartOptions();

      console.log(`created終了：${this.$moment().format('YYYY-MM-DD HH:mm:ss')}`);
      this.$store.commit('loading/end_loading');
    },
    methods: {
      // 月報をダウンロード
      async download() {
        if (!this.isDateSettingValid()) {
          return;
        }

        this.$store.commit('loading/start_loading');
        console.log(`download開始：${this.$moment().format('YYYY-MM-DD HH:mm:ss')}`);

        // サマリーデータを取得
        // サマリーデータの取得期間を設定
        const measureDateFrom = this.$moment(this.date).add(-11, 'months').startOf('month');
        let measureDateTo;
        if (this.$moment(this.date).endOf('month') < this.$moment()) {
          measureDateTo = this.$moment(this.date).endOf('month');
        } else {
          measureDateTo = this.$moment();
        }

        console.log(measureDateFrom.format('YYYY-MM-DD HH:mm:ss.SSSSSSS'));
        console.log(measureDateTo.format('YYYY-MM-DD HH:mm:ss.SSSSSSS'));
        console.log(`サマリー開始：${this.$moment().format('YYYY-MM-DD HH:mm:ss')}`);
        const dailySummaryData = await this.azureRequest('/api/getMonthlyReport', 'POST', {
          localeId: this.localeId,
          devices: this.devices.map((item) => {
            return {
              modelId: item.model_id,
              serialNo: item.serial_no,
            };
          }),
          measureDateFrom: measureDateFrom.format('YYYY-MM-DD'),
          measureDateTo: measureDateTo.format('YYYY-MM-DD'),
        });
        console.log(dailySummaryData);
        if (dailySummaryData === null) {
          this.$store.commit('loading/end_loading');
          return;
        }
        this.summaryData = dailySummaryData;
        console.log(`サマリー終了：${this.$moment().format('YYYY-MM-DD HH:mm:ss')}`);

        // テンプレートの読み込み
        const workBook = new exceljs.Workbook();
        await workBook.xlsx.load(this.monthlyReportTemplate);

        // メンテナンス履歴
        await this.processMaintenanceSheet(workBook);

        // データ一覧
        await this.processDataSheet(workBook);

        // グラフ
        await this.processGraphSheet(workBook);

        // 集計
        await this.processSummarySheet(workBook);

        // ファイルダウンロード
        const data = await workBook.xlsx.writeBuffer();
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `monthlyReport_${this.$moment(this.date).format('YYYY-MM')}.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);

        console.log(`download終了：${this.$moment().format('YYYY-MM-DD HH:mm:ss')}`);

        // 出力ログの保存
        // 何年何月分の月報を、機械1台分 or 設定グループ or 需要家全体
        const contents = {
          monthlyReport: {
            date: this.date,
            device: {
              modelId: this.modelId !== '' ? this.modelId : null,
              serialNo: this.serialNo !== '' ? this.serialNo : null,
            },
            group: {
              groupId: !['all', 'unset'].includes(this.groupId) ? this.groupId : null,
              groupName: !['all', 'unset'].includes(this.groupId) ? this.groupName : null,
            },
            // 需要家名はloggingメソッドで設定
            allDevices: this.groupId === 'all' ? true : null,
          },
        };

        await this.logging('monthlyReport', contents);

        this.$store.commit('loading/end_loading');
      },
      // メンテンナンス履歴シート用処理
      async processMaintenanceSheet(workBook) {
        const workSheet = workBook.getWorksheet(this.$t('maintenanceRecords'));
        // Smartpassのサービス実績情報を取得
        const reportData = [];
        const equipmentNos = this.devices.map((item) => {
          return `${item.name_locale2}`;
        });

        //Bodyで配列で受け渡しするように変更は必要${equipmentNos.join('&')}`
        const reports = await this.azureRequest(`/api/getLatestMaintenanceReport`, 'POST', equipmentNos);

        if (reports) {
          for (const report of reports) {
            // 試運転、年次点検、OH、保守点検のみ
            if (['1', '3', '4', '9'].includes(report.fservicecd)) {
              const device = this.devices.find((item) => item.name_locale2 === report.equipmentNo);
              reportData.push({
                optional_info_2: device.optional_info_2,
                name_locale: device[`name_locale${this.localeId}`],
                optional_info_1: device.optional_info_1,
                fservicecd: this.getFservicecdName(report.fservicecd),
                taskType: this.getTasktypeName(report.taskType),
                finishDate: report.finishDate,
                fkadotm: report.fkadotm,
              });
            }
          }
        }

        const beginRowIndex = 5;
        let currentIndex = beginRowIndex;
        for (let i = 0; i < reportData.length; i++) {
          // データをセット
          const row = workSheet.getRow(currentIndex);
          row.getCell(2).value = reportData[i].optional_info_2; // お客様管理号機
          row.getCell(3).value = reportData[i].name_locale; // 機械番号
          row.getCell(4).value = reportData[i].optional_info_1; // 型式
          row.getCell(5).value = reportData[i].fservicecd; // サービス実績区分
          row.getCell(6).value = reportData[i].taskType; // 作業内容
          row.getCell(7).value = reportData[i].finishDate; // 工事終了日
          row.getCell(8).value = reportData[i].fkadotm; // 運転時間
          currentIndex++;

          // 行の追加とスタイルのコピー
          workSheet.addRow(new Array(workSheet.columnCount).fill(null), 'i');
          workSheet.getRow(currentIndex).eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const style = JSON.parse(JSON.stringify(workSheet.getCell(beginRowIndex, colNumber).style));
            cell.style = style;
          });
        }

        workSheet.pageSetup.printArea = `A1:I${workSheet.rowCount}`;
      },
      // データ一覧シート用処理
      async processDataSheet(workBook) {
        const workSheet = workBook.getWorksheet(this.$t('dataList'));

        let beginDate = this.$moment(this.date).startOf('month');
        const endDate = this.$moment(this.date).endOf('month');
        workSheet.getCell('B3').value = this.$moment(this.date).format('YYYY/MM'); // 日付

        // 列のフォーマット（指定した月の日数分、列を追加）
        const beginDateColIndex = 5;
        for (; beginDate <= endDate; beginDate.add(1, 'days')) {
          const currentColIndex = beginDateColIndex + beginDate.date() - 1;

          // データの設定
          workSheet.getCell(3, currentColIndex).value = `${beginDate.format('D')}`; // 日
          workSheet.getCell(4, currentColIndex).value = beginDate.format('ddd'); // 曜日

          // 土・日の色を変更
          const style = JSON.parse(JSON.stringify(workSheet.getCell(3, beginDateColIndex).style));
          if (beginDate.day() === 0) {
            style.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF0000' },
              bgColor: { argb: 'FF0000' },
            };
            style.font.color = { argb: 'FFFFFF' };
          } else {
            style.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '1565c0' },
              bgColor: { argb: '1565c0' },
            };
            if (beginDate.day() === 6) {
              style.font.color = { argb: '000000' };
            } else {
              style.font.color = { argb: 'FFFFFF' };
            }
          }
          workSheet.getCell(3, currentColIndex).style = style;
          workSheet.getCell(4, currentColIndex).style = style;
        }

        // デバイスの数だけ、行のセットを追加
        for (let i = 1; i < this.devices.length; i++) {
          for (let j = 0; j < 8; j++) {
            workSheet.addRow(new Array(workSheet.columnCount).fill(null), 'i');
          }
        }

        // 行のフォーマット（背景色や罫線など）
        const beginRowIndex = 5;
        for (let i = 0; i < this.devices.length; i++) {
          const currentIndex = beginRowIndex + 8 * i;
          for (let j = 0; j < 8; j++) {
            workSheet.getRow(currentIndex + j).eachCell({ includeEmpty: true }, (cell, colNumber) => {
              const style = JSON.parse(JSON.stringify(workSheet.getCell(beginRowIndex + j, colNumber).style));

              // 偶数行は背景色を変更
              if (i % 2 === 1) {
                style.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: '99CCFF' },
                  bgColor: { argb: '99CCFF' },
                };
              }
              cell.value = workSheet.getCell(beginRowIndex + j, colNumber).value;
              cell.style = style;
            });

            // セルのマージ
            if (j === 0) workSheet.mergeCells([currentIndex + j, 1, currentIndex + j + 7, 1]);
          }
        }

        // 月トータルを追加
        const beginSumColIndex = beginDateColIndex + 31;
        workSheet.getCell(3, beginSumColIndex).value = this.$t('thisMonth');
        workSheet.getCell(4, beginSumColIndex).value = this.$t('summary');

        /* データを取得してシートにセットする */

        // シートに値を設定
        const workSheetForDataList = workBook.getWorksheet('データ一覧シート表示項目');
        beginDate = this.$moment(this.date).startOf('month');
        for (; beginDate <= endDate; beginDate.add(1, 'days')) {
          // 日付単位のループ

          const currentColIndex = beginDateColIndex + beginDate.date() - 1;
          for (let i = 0; i < this.devices.length; i++) {
            // デバイス単位のループ
            const currentIndex = beginRowIndex + 8 * i;

            const device = this.devices[i];
            const deviceInfo = [
              device.optional_info_2,
              device[`name_locale${this.localeId}`],
              device.optional_info_1,
            ].join('\r\n');
            workSheet.getCell(currentIndex, 1).value = deviceInfo;
            workSheet.getCell(currentIndex, 1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

            // データ一覧シート表示項目シートから表示するセンサを記載
            let ind = 0;
            workSheetForDataList.getColumn(1).eachCell({ includeEmpty: true }, (cell, rowNumber) => {
              if (cell.value === device.model_id) {
                if (workSheetForDataList.getCell(rowNumber, 2).value !== null) {
                  workSheet.getRow(currentIndex + ind).getCell(2).value = `${
                    workSheetForDataList.getCell(rowNumber, 2).value
                  }/${workSheetForDataList.getCell(rowNumber, 3).value}`;
                  workSheet.getRow(currentIndex + ind).getCell(3).value = `${
                    workSheetForDataList.getCell(rowNumber, 4).value
                  }/${workSheetForDataList.getCell(rowNumber, 5).value}`;
                  workSheet.getRow(currentIndex + ind).getCell(4).value = workSheetForDataList.getCell(
                    rowNumber,
                    6
                  ).value;
                }
                ind++;
              }
            });

            // サマリーデータから、modelId、serialNo、measureDateでデータを取り出す
            const deviceData = this.summaryData.filter(
              (item) =>
                item.modelId === device.model_id &&
                item.serialNo === device.serial_no &&
                item.measureDate === beginDate.format('YYYY-MM-DD')
            );
            if (deviceData.length === 0) {
              continue;
            }

            for (let j = 0; j < 8; j++) {
              // センサ単位のループ
              const row = workSheet.getRow(currentIndex + j);
              const label = row.getCell(2).value;
              if (label === null) continue;

              // データからラベルとタイプが一致しているものを取得
              const targets = deviceData[0].sensors.filter((item) => item.label === label.split('/')[0]);

              // 該当したデータをセット
              if (targets.length > 0) {
                const target = targets[0];
                workSheet.getCell(currentIndex + j, currentColIndex).value = Number(target.data);
              }
            }
          }
        }

        // 月トータルを計算
        let deviceIndex = 0;
        workSheet.getColumn(beginSumColIndex).eachCell({ includeEmpty: true }, (cell, rowNumber) => {
          if (rowNumber >= 5) {
            const row = workSheet.getRow(rowNumber);
            const label = row.getCell(2).value;
            if (label === null) return;

            const array = [];
            beginDate = this.$moment(this.date).startOf('month');
            for (; beginDate <= endDate; beginDate.add(1, 'days')) {
              const currentColIndex = beginDateColIndex + beginDate.date() - 1;
              const value = workSheet.getCell(rowNumber, currentColIndex).value;

              if (label.split('/')[0] === '負荷状態') {
                array.push(value);
              } else {
                if (value !== null && value !== 0) {
                  array.push(value); // 運転中のみ
                }
              }
            }
            const operator = row.getCell(3).value;
            cell.value = this.getSummaryValue(operator.split('/')[0], array);
            if (String(label).includes('吐出圧力')) {
              // サマリーシートで吐出圧力を出すため
              this.devices[deviceIndex].dischargePressure = this.getSummaryValue(operator.split('/')[0], array);
              deviceIndex++;
            }

            // 項目名を言語別表記に
            row.getCell(2).value = label.split('/')[1];
            row.getCell(3).value = operator.split('/')[1];
          }
        });

        workBook.removeWorksheet(workSheetForDataList.id);
        workSheet.pageSetup.printArea = `A1:${
          workSheet.getCell(5 + 8 * this.devices.length, beginDateColIndex + 31).address
        }`;
      },
      // グラフシート用処理
      async processGraphSheet(workBook) {
        const workSheet = workBook.getWorksheet(this.$t('graph'));

        workSheet.getCell('AW1').value = this.$moment(this.date).format('YYYY/MM'); // 日付

        /* 日毎 */
        const beginDateColIndex = 7;

        // 値をシートにセット
        this.thisMonthAirConsumption = 0;
        this.thisMonthPowerConsumption = 0;
        let beginDate = this.$moment(this.date).startOf('month');
        const endDate = this.$moment(this.date).endOf('month');
        for (; beginDate <= endDate; beginDate.add(1, 'days')) {
          const dailyData = this.summaryData.filter((item) => item.measureDate === beginDate.format('YYYY-MM-DD'));
          if (dailyData.length === 0) {
            continue;
          }

          let dailyAirConsumption = 0;
          let dailyPowerConsumption = 0;
          for (const data of dailyData) {
            let targets = data.sensors.filter((item) => item.label === '吐出空気量');
            if (targets.length > 0) {
              dailyAirConsumption += Number(targets[0].data);
            }
            targets = data.sensors.filter((item) => item.label === '消費電力量');
            if (targets.length > 0) {
              dailyPowerConsumption += Number(targets[0].data);
            }
          }

          const currentColIndex = beginDateColIndex + beginDate.date() - 1;
          this.thisMonthAirConsumption += dailyAirConsumption;
          this.thisMonthPowerConsumption += dailyPowerConsumption;
          workSheet.getCell(20, currentColIndex).value = dailyAirConsumption;
          workSheet.getCell(40, currentColIndex).value = dailyPowerConsumption;
          if (dailyAirConsumption !== 0) {
            workSheet.getCell(41, currentColIndex).value = dailyPowerConsumption / dailyAirConsumption;
          }
        }

        // 今月の消費空気量
        beginDate = this.$moment(this.date).startOf('month');
        const dailyAirConsumptionDateRow = workSheet.getRow(19);
        const dailyAirConsumptionDataList = [];
        for (let i = 0; i < 31; i++) {
          const currentColIndex = beginDateColIndex + i;
          dailyAirConsumptionDateRow.getCell(currentColIndex).value = beginDate <= endDate ? beginDate.date() : '';
          dailyAirConsumptionDataList.push({
            x: beginDate.toISOString(true),
            y: beginDate <= endDate ? Number(workSheet.getCell(20, currentColIndex).value) : 0,
          });
          beginDate.add(1, 'days');
        }
        await this.$refs.dailyAirConsumptionChart.updateSeries([
          {
            name: this.$t('dischargeAirVolume'),
            data: dailyAirConsumptionDataList,
          },
        ]);
        await this.addImage(workBook, workSheet, this.$refs.dailyAirConsumptionChart, 1052, 275, { col: 1, row: 4 });

        // 今月の消費電力量/原単位
        beginDate = this.$moment(this.date).startOf('month');
        const dailyPowerConsumptionDateRow = workSheet.getRow(39);
        const dailyPowerConsumptionDataList1 = [];
        const dailyPowerConsumptionDataList2 = [];
        for (let i = 0; i < 31; i++) {
          const currentColIndex = beginDateColIndex + i;
          dailyPowerConsumptionDateRow.getCell(currentColIndex).value = beginDate <= endDate ? beginDate.date() : '';
          dailyPowerConsumptionDataList1.push({
            x: beginDate.toISOString(true),
            y: beginDate <= endDate ? Number(workSheet.getCell(40, currentColIndex).value) : 0,
          });
          dailyPowerConsumptionDataList2.push({
            x: beginDate.toISOString(true),
            y: beginDate <= endDate ? Number(workSheet.getCell(41, currentColIndex).value) : 0,
          });
          beginDate.add(1, 'days');
        }
        this.setPowerConsumptionYAxisManually(this.$refs.dailyPowerConsumptionChart, dailyPowerConsumptionDataList1);
        await this.$refs.dailyPowerConsumptionChart.updateSeries([
          {
            name: this.$t('powerConsumption'),
            type: 'column',
            data: dailyPowerConsumptionDataList1,
          },
          {
            name: this.$t('energyUnit'),
            type: 'line',
            data: dailyPowerConsumptionDataList2,
          },
        ]);
        await this.addImage(workBook, workSheet, this.$refs.dailyPowerConsumptionChart, 1165, 275, { col: 1, row: 24 });

        /* 月間 */
        const beginMonthColIndex = 43;

        // 値をシートにセット
        let beginMonth = this.$moment(this.date).add(-11, 'months');
        for (let i = 0; i < 12; i++) {
          const monthlyData = this.summaryData.filter(
            (item) => item.measureDate.substring(0, 7) === beginMonth.format('YYYY-MM')
          );
          if (monthlyData.length === 0) {
            beginMonth.add(1, 'months');
            continue;
          }

          let monthlyAirConsumption = 0;
          let monthlyPowerConsumption = 0;
          for (const data of monthlyData) {
            let targets = data.sensors.filter((item) => item.label === '吐出空気量');
            if (targets.length > 0) {
              monthlyAirConsumption += Number(targets[0].data);
            }
            targets = data.sensors.filter((item) => item.label === '消費電力量');
            if (targets.length > 0) {
              monthlyPowerConsumption += Number(targets[0].data);
            }
          }

          workSheet.getCell(20, beginMonthColIndex + i).value = monthlyAirConsumption;
          workSheet.getCell(40, beginMonthColIndex + i).value = monthlyPowerConsumption;
          if (monthlyAirConsumption > 0) {
            workSheet.getCell(41, beginMonthColIndex + i).value = monthlyPowerConsumption / monthlyAirConsumption;
          }

          beginMonth.add(1, 'months');
        }

        // 月間消費空気量推移
        beginMonth = this.$moment(this.date).add(-11, 'months');
        const monthlyAirConsumptionMonthRow = workSheet.getRow(19);
        const monthlyAirConsumptionDataList = [];
        for (let i = 0; i < 12; i++) {
          monthlyAirConsumptionMonthRow.getCell(beginMonthColIndex + i).value = beginMonth.format('M');
          monthlyAirConsumptionDataList.push({
            x: beginMonth.toISOString(true),
            y: Number(workSheet.getCell(20, beginMonthColIndex + i).value),
          });
          beginMonth.add(1, 'months');
        }
        await this.$refs.monthlyAirConsumptionChart.updateSeries([
          {
            name: this.$t('dischargeAirVolume'),
            data: monthlyAirConsumptionDataList,
          },
        ]);
        await this.addImage(workBook, workSheet, this.$refs.monthlyAirConsumptionChart, 1067, 275, {
          col: 38,
          row: 4,
        });

        // 月間消費電力量/原単位推移
        beginMonth = this.$moment(this.date).add(-11, 'months');
        const monthlyPowerConsumptionMonthRow = workSheet.getRow(39);
        const monthlyPowerConsumptionDataList1 = [];
        const monthlyPowerConsumptionDataList2 = [];
        for (let i = 0; i < 12; i++) {
          monthlyPowerConsumptionMonthRow.getCell(beginMonthColIndex + i).value = beginMonth.format('M');
          monthlyPowerConsumptionDataList1.push({
            x: beginMonth.toISOString(true),
            y: Number(workSheet.getCell(40, beginMonthColIndex + i).value),
          });
          monthlyPowerConsumptionDataList2.push({
            x: beginMonth.toISOString(true),
            y: Number(workSheet.getCell(41, beginMonthColIndex + i).value),
          });
          beginMonth.add(1, 'months');
        }
        this.setPowerConsumptionYAxisManually(
          this.$refs.monthlyPowerConsumptionChart,
          monthlyPowerConsumptionDataList1
        );
        await this.$refs.monthlyPowerConsumptionChart.updateSeries([
          {
            name: this.$t('powerConsumption'),
            type: 'column',
            data: monthlyPowerConsumptionDataList1,
          },
          {
            name: this.$t('energyUnit'),
            type: 'line',
            data: monthlyPowerConsumptionDataList2,
          },
        ]);
        await this.addImage(workBook, workSheet, this.$refs.monthlyPowerConsumptionChart, 1180, 275, {
          col: 38,
          row: 24,
        });

        await workSheet.protect('wz5J259wuaRB');
      },
      // サマリーシート用処理
      async processSummarySheet(workBook) {
        const workSheet = workBook.getWorksheet(this.$t('summary'));

        // UTCで開始・終了日時を設定
        const beginDate = this.$moment(this.$moment(this.date).startOf('month').format('YYYY-MM-DD 00:00:00.0000000'));
        const utcBeginDate = this.$moment.utc(beginDate);
        let endDate;

        // ヘッダ
        workSheet.getCell('B3').value = this.deviceGroupName; // 会社名
        let term = `${this.$moment(this.date).format('YYYY/MM/01')}～`;
        if (this.$moment(this.date).endOf('month') < this.$moment()) {
          term += this.$moment(this.date).endOf('month').format('DD');
          endDate = this.$moment(this.$moment(this.date).endOf('month').format('YYYY-MM-DD 23:59:59.9999999'));
        } else {
          term += this.$moment().format('DD');
          endDate = this.$moment(this.$moment().format('YYYY-MM-DD 23:59:59.9999999'));
        }
        const utcEndDate = this.$moment.utc(endDate);

        workSheet.getCell('C4').value = term; // 期間
        workSheet.getCell('C5').value = this.groupName; // グループ名
        workSheet.getCell('I3').value = this.thisMonthAirConsumption; // 消費空気量
        workSheet.getCell('I4').value = this.thisMonthPowerConsumption; // 消費電力量
        if (this.thisMonthAirConsumption > 0) {
          workSheet.getCell('I5').value = this.thisMonthPowerConsumption / this.thisMonthAirConsumption; // 原単位
        }
        const utcOffset = this.$moment().utcOffset();
        const utcOffsetHours = utcOffset / 60; // utcOffset（時）
        const utcOffsetMinutes = utcOffset % 60; // utcOffset（分）
        const utc = `UTC${utcOffset > 0 ? '+' : '-'}${('00' + utcOffsetHours).slice(-2)}${(
          '00' + utcOffsetMinutes
        ).slice(-2)}`;
        workSheet.getCell('O3').value = `${this.$moment().format('YYYY/MM/DD HH:mm:ss')} (${utc})`; // 出力時間（タイムゾーン含む）
        workSheet.getCell('O4').value = this.user_info.userId; // 出力ユーザ

        const eventIncidentHistories = [];
        for (let i = 0; i < this.devices.length; i++) {
          const device = this.devices[i];

          // イベント・アラームデータを取得
          const eventIncidentHistoryData = await this.apiRequest('/v1/alarm-event-mgt/eventIncidentHistory', 'GET', {
            get_class: '4',
            sort: 'event_time',
            event_level: '0,1,2,3',
            incident_class: 1,
            max_number: 2000,
            event_time_from: utcBeginDate.format('YYYY-MM-DD HH:mm:ss.SSSSSSS'),
            event_time_to: utcEndDate.format('YYYY-MM-DD HH:mm:ss.SSSSSSS'),
            'device_list[0].model_id': device.model_id,
            'device_list[0].serial_no': device.serial_no,
          });
          if (eventIncidentHistoryData !== null) {
            // 重複データを取り除く
            const eventIncidentHistory = eventIncidentHistoryData.filter(
              (el, ind, self) => self.findIndex((e) => e.event_time === el.event_time) === ind
            );
            // お客様管理号機, 機械番号, 型式を追加
            eventIncidentHistory.forEach((item) => {
              item.device_optional_info_2 = device.optional_info_2;
              item.device_name_locale = device[`name_locale${this.localeId}`];
              item.device_optional_info_1 = device.optional_info_1;
            });
            eventIncidentHistories.push(...eventIncidentHistory);
          }

          // 稼働時間、起動回数を取得
          const sensorData = await this.apiRequest('/v1/sensor-data-file-mgt/getSensorData', 'POST', {
            get_class: 1,
            locale_id: this.localeId,
            max_number: 30000,
            measure_time_from: utcBeginDate.format('YYYY-MM-DD HH:mm:ss.SSSSSSS'),
            measure_time_to: utcEndDate.format('YYYY-MM-DD HH:mm:ss.SSSSSSS'),
            round_kbn: 1,
            sensor_list: device.sensors.map((sensor) => {
              return {
                model_id: sensor.model_id,
                sensor_id: sensor.sensor_id,
                serial_no: sensor.serial_no,
              };
            }),
            iso8601_datetime_format: true,
          });
          if (sensorData === null) {
            this.$store.commit('loading/end_loading');
            return;
          }

          // 稼働時間、起動回数を計算して設定
          // ただし、PowerがONのものだけ（PowerはLED ランプ状態(Hex)の16bitの下位8bit）
          const runningHourSensor = device.sensors.filter((x) => x.name_locale2 === '運転時間');
          const numberOfTimesToStartSensor = device.sensors.filter((x) => x.name_locale2 === '総運転回数');
          const panelLampSensor = device.sensors.filter((x) => x.name_locale2 === 'LED ランプ状態(Hex)');

          // 各センサデータ
          const runningHourSensorData = sensorData[0].timeList.filter(
            (x) => x.sensorId === runningHourSensor[0].sensor_id
          );
          let numberOfTimesToStartSensorData = [];
          if (numberOfTimesToStartSensor.length > 0) {
            numberOfTimesToStartSensorData = sensorData[0].timeList.filter(
              (x) => x.sensorId === numberOfTimesToStartSensor[0].sensor_id
            );
          }
          const panelLampSensorData = sensorData[0].timeList.filter((x) => x.sensorId === panelLampSensor[0].sensor_id);

          // LED ランプ状態(Hex)のbit列16桁の下1桁がPowerのフラグなので、
          // これが1(=ON)の場合だけ、データとして認める
          const runningHourDataList = [];
          const numberOfTimesToStartDataList = [];
          for (const panelLampData of panelLampSensorData[0].dataList) {
            const bitStr = ('0'.repeat(16) + Number(panelLampData.data).toString(2)).slice(-16);
            const bitArray = bitStr.match(/.{1}/g);
            if (bitArray[bitArray.length - 1] !== '1') continue;

            const runningHourData = runningHourSensorData[0].dataList.filter(
              (x) => x.measureTime === panelLampData.measureTime
            );
            if (runningHourData.length > 0) {
              runningHourDataList.push(runningHourData[0].data);
            }
            if (numberOfTimesToStartSensorData.length > 0) {
              const numberOfTimesToStartData = numberOfTimesToStartSensorData[0].dataList.filter(
                (x) => x.measureTime === panelLampData.measureTime
              );
              if (numberOfTimesToStartData.length > 0) {
                numberOfTimesToStartDataList.push(numberOfTimesToStartData[0].data);
              }
            }
          }

          if (runningHourDataList.length > 0) {
            device.totalRunningHour = Number(runningHourDataList[runningHourDataList.length - 1]);
            device.thisMonthRunningHour =
              Number(runningHourDataList[runningHourDataList.length - 1]) - Number(runningHourDataList[0]);
          }
          if (numberOfTimesToStartDataList.length > 0) {
            device.totalNumberOfTimesToStart = Number(
              numberOfTimesToStartDataList[numberOfTimesToStartDataList.length - 1]
            );
            device.thisMonthNumberOfTimesToStart =
              Number(numberOfTimesToStartDataList[numberOfTimesToStartDataList.length - 1]) -
              Number(numberOfTimesToStartDataList[0]);
          }
        }

        // データをセット
        const beginRowIndex = 10;
        const maxRowCount =
          this.devices.length > eventIncidentHistories.length ? this.devices.length : eventIncidentHistories.length;
        let currentIndex = beginRowIndex;
        for (let i = 0; i < maxRowCount; i++) {
          const row = workSheet.getRow(currentIndex);
          if (this.devices.length > i) {
            row.getCell(2).value = this.devices[i].optional_info_2; // お客様管理号機
            row.getCell(3).value = this.devices[i][`name_locale${this.localeId}`]; // 機械番号
            row.getCell(4).value = this.devices[i].optional_info_1; // 型式
            row.getCell(5).value = this.devices[i].totalRunningHour; // 稼働時間(Hr)（累計）
            row.getCell(6).value = this.devices[i].thisMonthRunningHour; // 稼働時間(Hr)（当月）
            row.getCell(7).value = this.devices[i].totalNumberOfTimesToStart; // 起動回数(Hr)（累計）
            row.getCell(8).value = this.devices[i].thisMonthNumberOfTimesToStart; // 起動回数(Hr)（当月）
            row.getCell(9).value = this.devices[i].dischargePressure; // 平均吐出圧力(MPa)
          }
          if (eventIncidentHistories.length > i) {
            row.getCell(11).value = eventIncidentHistories[i].device_optional_info_2; // お客様管理号機
            row.getCell(12).value = eventIncidentHistories[i].device_name_locale; // 機械番号
            row.getCell(13).value = eventIncidentHistories[i].device_optional_info_1; // 型式
            row.getCell(14).value = this.$moment
              .utc(eventIncidentHistories[i].event_time)
              .utcOffset(utcOffset)
              .format('YYYY/MM/DD HH:mm:ss'); // 発生時刻
            row.getCell(15).value = eventIncidentHistories[i][`name_locale${this.localeId}`]; // 内容
          }
          currentIndex++;

          // 行の追加とスタイルのコピー
          workSheet.addRow(new Array(workSheet.columnCount).fill(null), 'i');
          workSheet.getRow(currentIndex).eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const style = JSON.parse(JSON.stringify(workSheet.getCell(beginRowIndex, colNumber).style));
            cell.style = style;
          });
        }

        workSheet.pageSetup.printArea = `A1:P${workSheet.rowCount + 1}`;
      },
      // グラフの設定を行う
      async setChartOptions() {
        // 数字に3桁ごとにカンマを追加する
        const putComma = (val) => {
          return String(val.toFixed(0)).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
        };

        // デフォルトオプションを取得
        this.dailyAirConsumptionChartOptions = this.$refs.dailyAirConsumptionChart.getOptions();
        this.monthlyAirConsumptionChartOptions = this.$refs.monthlyAirConsumptionChart.getOptions();
        this.dailyPowerConsumptionChartOptions = this.$refs.dailyPowerConsumptionChart.getOptions();
        this.monthlyPowerConsumptionChart = this.$refs.monthlyPowerConsumptionChart.getOptions();

        // y軸設定
        // 日別吐出空気量
        const yAxisDay = JSON.parse(JSON.stringify(this.dailyAirConsumptionChartOptions.yaxis[0]));
        yAxisDay.title.text = `${this.$t('dischargeAirVolume')} (ｍ³)`;
        yAxisDay.decimalsInFloat = 0;
        yAxisDay.forceNiceScale = true;
        yAxisDay.labels.minWidth = 113;
        yAxisDay.labels.maxWidth = 113;
        yAxisDay.labels.formatter = putComma;
        this.dailyAirConsumptionChartOptions.yaxis[0] = yAxisDay;

        // 月別吐出空気量
        const yAxisMonth = JSON.parse(JSON.stringify(yAxisDay));
        yAxisMonth.title.text = `${this.$t('dischargeAirVolume')} (ｍ³)`;
        yAxisMonth.labels.minWidth = 100;
        yAxisMonth.labels.maxWidth = 100;
        yAxisMonth.labels.formatter = putComma;
        this.monthlyAirConsumptionChartOptions.yaxis[0] = yAxisMonth;

        // 日別消費電力量/原単位
        const yAxisDay1 = JSON.parse(JSON.stringify(yAxisDay));
        const yAxisDay2 = JSON.parse(JSON.stringify(yAxisDay));
        yAxisDay1.title.text = `${this.$t('powerConsumption')} (kWh)`;
        yAxisDay1.seriesName = this.$t('powerConsumption');
        yAxisDay1.labels.formatter = putComma;
        yAxisDay1.labels.minWidth = 147;
        yAxisDay1.labels.maxWidth = 147;

        yAxisDay2.title.text = `${this.$t('energyUnit')} (kWh/ｍ³)`;
        yAxisDay2.seriesName = this.$t('energyUnit');
        yAxisDay2.decimalsInFloat = 3;
        yAxisDay2.opposite = true;
        yAxisDay2.labels.minWidth = 50;
        yAxisDay2.labels.maxWidth = 50;
        this.dailyPowerConsumptionChartOptions.yaxis = [yAxisDay1, yAxisDay2];

        // 月別消費電力量/原単位
        const yAxisMonth1 = JSON.parse(JSON.stringify(yAxisDay1));
        const yAxisMonth2 = JSON.parse(JSON.stringify(yAxisDay2));
        yAxisMonth1.labels.formatter = putComma;
        yAxisMonth1.labels.minWidth = 140;
        yAxisMonth1.labels.maxWidth = 140;
        this.monthlyPowerConsumptionChart.yaxis = [yAxisMonth1, yAxisMonth2];

        // x軸設定
        this.dailyAirConsumptionChartOptions.xaxis.labels.show = false;
        this.monthlyAirConsumptionChartOptions.xaxis.labels.show = false;
        this.dailyPowerConsumptionChartOptions.xaxis.labels.show = false;
        this.monthlyPowerConsumptionChart.xaxis.labels.show = false;

        // 凡例
        this.dailyAirConsumptionChartOptions.legend.show = false;
        this.monthlyAirConsumptionChartOptions.legend.show = false;
        this.dailyPowerConsumptionChartOptions.legend.show = false;
        this.monthlyPowerConsumptionChart.legend.show = false;

        // チャートオプションを更新
        await this.$refs.dailyAirConsumptionChart.updateOptions(this.dailyAirConsumptionChartOptions);
        await this.$refs.monthlyAirConsumptionChart.updateOptions(this.monthlyAirConsumptionChartOptions);
        await this.$refs.dailyPowerConsumptionChart.updateOptions(this.dailyPowerConsumptionChartOptions);
        await this.$refs.monthlyPowerConsumptionChart.updateOptions(this.monthlyPowerConsumptionChart);
      },
      // forceNiceScaleが効かないので、消費電力のy軸を手動で設定する
      async setPowerConsumptionYAxisManually(chart, datalist) {
        const maxY = Math.max(...datalist.map((item) => item.y));
        const exponent = Math.floor(Math.log10(maxY));
        const niceBase = Math.pow(10, exponent);

        const options = chart.getOptions();
        options.yaxis[0].forceNiceScale = false;
        options.yaxis[0].min = 0;
        options.yaxis[0].max = Math.ceil(maxY / niceBase) * niceBase;
        await chart.updateOptions(options);
      },
      // 配列をサマリーした結果を返す
      getSummaryValue(type, array) {
        if (array.length === 0) {
          return 0;
        }
        let result = 0;
        switch (type) {
          case '最高':
            result = array.reduce((a, b) => Math.max(a, b));
            break;
          case '最低':
            result = array.reduce((a, b) => Math.min(a, b));
            break;
          case '平均':
            result = array.reduce((a, b) => a + b) / array.length;
            break;
          case '合計':
            result = array.reduce((a, b) => a + b);
            break;
        }
        return result;
      },
      // エクセルシートに画像を追加する
      async addImage(workBook, workSheet, chart, width, height, position) {
        // base64に変換
        const dataURI = await chart.dataURI();

        // シートに画像を追加
        const imageId = workBook.addImage({
          base64: dataURI.imgURI,
          extension: 'png',
        });
        workSheet.addImage(imageId, {
          tl: position,
          ext: { width: width, height: height },
          editAs: 'absolute',
        });
      },
      // ぱんくずリストクリック
      selectedGroupChange(group) {
        this.$router.push({
          name: group.children.length > 0 ? 'HomeAdmin' : 'HomeConsumer',
          params: { device_group_id: group.device_group_id },
        });
      },
      selectedDeviceChange(device) {
        this.$router.push({
          name: 'CompressorInfo',
          params: { model_id: device.model_id, serial_no: device.serial_no },
        });
      },
      isDateSettingValid() {
        if (this.$refs.Cal.hasError) {
          this.$store.commit('snackbar/show_snackbar', {
            text: this.$t('invalidDateEntered'),
            color: 'error',
            timeout: 5000,
            multiline: false,
          });
          return false;
        } else {
          return true;
        }
      },
      // サービス業績区分
      getFservicecdName(code) {
        switch (code) {
          case '1':
            return this.$t('commissioning');
          case '2':
            return this.$t('patrol');
          case '3':
            return this.$t('3000Hr_6000Hr_12000HrMaintenance');
          case '4':
            return this.$t('overhaul');
          case '5':
            return this.$t('warrantyClaimRelated');
          case '6':
            return this.$t('preventiveMaintenance');
          case '7':
            return this.$t('emergencyCallCheckup');
          case '8':
            return this.$t('partsChangeOthersEtc');
          case '9':
            return this.$t('patrolServiceCleanup');
        }
      },
      // 作業内容
      getTasktypeName(code) {
        switch (code) {
          case '99':
            return '--';
          case '101':
            return this.$t('commissioning');
          case '201':
            return this.$t('patrol');
          case '301':
            return this.$t('2000Hr');
          case '302':
            return this.$t('3000Hr');
          case '303':
            return this.$t('4000Hr');
          case '304':
            return this.$t('6000Hr');
          case '305':
            return this.$t('8000Hr');
          case '306':
            return this.$t('12000Hr');
          case '307':
            return this.$t('16000Hr');
          case '308':
            return this.$t('24000Hr');
          case '309':
            return this.$t('36000Hr');
          case '310':
            return this.$t('halfYearMaintenance');
          case '311':
            return this.$t('1YearMaintenance');
          case '312':
            return this.$t('2YearMaintenance');
          case '313':
            return this.$t('3YearMaintenance');
          case '314':
            return this.$t('4YearMaintenance');
          case '315':
            return this.$t('6YearMaintenance');
          case '316':
            return this.$t('4YearAnd_6YearMaintenance');
          case '317':
            return this.$t('8YearMaintenance');
          case '318':
            return this.$t('18000Hr');
          case '399':
            return this.$t('annualMaintenance');
          case '401':
            return this.$t('claim');
          case '402':
            return this.$t('preventiveMaintenance');
          case '501':
            return this.$t('irregularMaintenance');
          case '502':
            return this.$t('others');
          case '503':
            return this.$t('patrolService');
          case '901':
            return this.$t('oh_2ndStage');
          case '902':
            return this.$t('oh_1stStage');
          case '903':
            return this.$t('oh_1stAnd_2ndStage');
          case '904':
            return this.$t('oh');
          case '905':
            return this.$t('oh_1stAnd_2ndStage_6YearMaintenance');
          case '906':
            return this.$t('oh_36000Hr');
          case '907':
            return this.$t('oh_6YearMaintenance');
          case '908':
            return this.$t('ohFourYears');
          case '909':
            return this.$t('ohEightYears');
          case '999':
            return this.$t('regularMaintenance');
        }
      },
      // 言語変更
      setLocale() {
        switch (this.$i18n.locale) {
          case 'en':
            this.localeId = 1;
            this.monthlyReportTemplate = monthlyReportTemplateEn;
            break;
          case 'ja':
            this.localeId = 2;
            this.monthlyReportTemplate = monthlyReportTemplateJa;
            break;
          case 'zh':
            this.localeId = 3;
            // this.monthlyReportTemplate = monthlyReportTemplateZh
            break;
        }
      },
    },
  };
</script>

<style scoped>
  .chart {
    width: 500px;
    height: 200px;
    position: absolute;
    top: -9999px;
    left: -9999px;
  }
</style>
